import React, { useEffect, useState, useMemo } from 'react';
import {
    Calendar, Clock, Award, Coins, Video, Download, Star, Target, Settings,
    Mail, Phone, User, LogOut, Search, Grid, List, ChevronDown, CheckCircle2,
    ChevronLeft, ChevronRight, Share2, LayoutDashboard, Ticket, FileBadge, Gift,
    MoreVertical, ArrowRight, X, Edit3, MapPin, FileText, Heart, ShieldCheck,
    HelpCircle, Users, Plus, ExternalLink, Sparkles
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchUserDetails } from './fetchUserDetails';
import toast, { Toaster } from 'react-hot-toast';
import { submitCustomerReview, getUserReviewedBookingIds } from '../../admin/adminApis';
import { userApi } from '../../config';

interface profileData {
    _id: string;
    createAt: string;
    firstname: string;
    lastname: string;
    useremail: string;
    profileIcon: string;
    bio?: string;
    location?: string;
    phone?: string;
    dob?: string;
}

interface bookingData {
    _id: string;
    bookingId?: string;
    classId: string;
    classname?: string;
    bookingDate?: string;
    selectedDates?: string[];
    coverageStartDate?: string;
    createdAt: string;
    status: string;
    isDemo?: boolean;
    preferredTimeSlot: string;
    age: string;
    phone: string;
    specialRequirements: string;
}

interface classData {
    _id: string;
    classTitle?: string;
    title?: string;
    icon?: string;
    serviceName?: string;
    consultationTitle?: string;
    treatmentName?: string;
    packageName?: string;
    testName?: string;
    vendorName?: string;
    firstname?: string;
    lastname?: string;
    firstName?: string;
    lastName?: string;
    profileIcon?: string;
    classCategory?: string;
    classDescription?: string;
    classStartDate?: string;
    classEndDate?: string;
    classDuration?: string;
    classPrice?: any;
    classThumbnail?: string;
    thumbnail?: string;
    image?: string;
    photo?: string;
    googleMeetLinks?: Array<{
        id: string;
        time: string;
        title: string;
        url: string;
    }>;
    purchaseStatus: string;
    isDemo: boolean;
    demoCompleted: boolean;
}

interface soundTherapyBookingData {
    _id: string;
    bookingId: string;
    bookingDate: string;
    timeSlot: string;
    totalAmount: number;
    status: string;
    createdAt?: string;
}

interface dmitBookingData {
    _id: string;
    bookingId: string;
    bookingDate: string;
    timeSlot: string;
    totalAmount: number;
    status: string;
    createdAt?: string;
}

interface labTestBookingData {
    _id: string;
    testName: string;
    testCategory: string;
    name: string;
    email: string;
    phone: string;
    age: string;
    amount: number;
    paymentStatus: string;
    bookingStatus: string;
    createdAt: string;
}

interface userData {
    user: profileData;
    bookings: bookingData[];
    classes: classData[];
    soundTherapyBookings: soundTherapyBookingData[];
    dmitBookings: dmitBookingData[];
    labTestBookings?: labTestBookingData[];
    consultationBookings?: any[];
    psychologyBookings?: any[];
    treatmentBookings?: any[];
    message?: string;
}

export interface UnifiedBooking {
    id: string;
    type: string;
    title: string;
    instructor: string;
    date: Date;
    duration: string;
    thumbnail: string;
    status: 'Upcoming' | 'Live' | 'Completed' | 'Cancelled';
    progressPercentage: number;
    currentDay: number;
    totalDay: number;
    joinUrl: string | null;
    createdAt: Date;
    classId?: string;
}

const CACHE_KEY = "userProfileCache";
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes in ms

export default function UserProfile() {
    const [activeTab, setActiveTab] = useState('My Profile');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [profile, setProfile] = useState({
        fullName: '',
        email: '',
        profileImage: '',
        create_at: "",
        bio: "",
        location: "",
        phone: "",
        dob: ""
    });

    const [serverData, setServerData] = useState<userData | null>(null);
    const [unifiedBookings, setUnifiedBookings] = useState<UnifiedBooking[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All Categories');
    const [statusFilter, setStatusFilter] = useState('All Status');
    const [dateFilter, setDateFilter] = useState('All Dates');

    // Customer Review State for Completed Bookings
    const [reviewedBookingIds, setReviewedBookingIds] = useState<string[]>([]);
    const [reviewModalBooking, setReviewModalBooking] = useState<UnifiedBooking | null>(null);
    const [reviewRating, setReviewRating] = useState<number>(5);
    const [reviewHoverRating, setReviewHoverRating] = useState<number>(0);
    const [reviewComment, setReviewComment] = useState<string>('');
    const [isSubmittingReview, setIsSubmittingReview] = useState<boolean>(false);

    // Goals & Social Links Modals
    const [goals, setGoals] = useState<Array<{ emoji: string; text: string; color: string }>>([
        { emoji: '🌿', text: 'Teach Yoga', color: 'emerald' },
        { emoji: '👥', text: 'Build Community', color: 'blue' },
        { emoji: '🧡', text: 'Help People Live Better', color: 'amber' },
        { emoji: '📈', text: 'Grow My Classes', color: 'purple' }
    ]);
    const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
    const [newGoalText, setNewGoalText] = useState('');

    const [socialLinks, setSocialLinks] = useState({
        instagram: 'https://instagram.com',
        youtube: 'https://youtube.com',
        facebook: 'https://facebook.com',
        linkedin: 'https://linkedin.com'
    });
    const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Check if ?tab=wishlist or state.activeTab is passed
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam === 'wishlist' || tabParam === 'Saved / Wishlist') {
            setActiveTab('Saved / Wishlist');
        } else if (location.state && (location.state as any).activeTab) {
            setActiveTab((location.state as any).activeTab);
        }
    }, [location.search, location.state]);

    // Saved Classes State (from localStorage)
    const [savedClasses, setSavedClasses] = useState<any[]>(() => {
        try {
            const raw = localStorage.getItem('metafit_saved_classes');
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            return [];
        }
    });

    // Re-sync saved classes whenever activeTab is 'Saved / Wishlist' or window receives storage event
    useEffect(() => {
        const loadSaved = async () => {
            let list: any[] = [];
            try {
                const raw = localStorage.getItem('metafit_saved_classes');
                if (raw) list = JSON.parse(raw);
            } catch (e) {
                list = [];
            }

            // Sync with backend if user is logged in
            if (sessionStorage.getItem('userAccessToken')) {
                try {
                    const res = await userApi.get<any>('/wishlist');
                    const resData = res.data as any;
                    if (resData?.success && Array.isArray(resData.wishlist)) {
                        const backendList = resData.wishlist;
                        const map = new Map();
                        backendList.forEach((c: any) => map.set(c._id || c.id, c));
                        list.forEach((c: any) => {
                            const id = c._id || c.id;
                            if (id && !map.has(id)) map.set(id, c);
                        });
                        const merged = Array.from(map.values());
                        localStorage.setItem('metafit_saved_classes', JSON.stringify(merged));
                        list = merged;

                        if (merged.length > backendList.length) {
                            await userApi.post('/wishlist/sync', { items: merged });
                        }
                    }
                } catch (err) {
                    console.error("Error syncing profile wishlist:", err);
                }
            }

            setSavedClasses(list);
        };

        if (activeTab === 'Saved / Wishlist') {
            loadSaved();
        }

        window.addEventListener('storage', loadSaved);
        return () => window.removeEventListener('storage', loadSaved);
    }, [activeTab]);

    const handleRemoveFromWishlist = async (classId: string) => {
        const itemToRemove = savedClasses.find(c => (c._id || c.id) === classId);
        const updated = savedClasses.filter(c => (c._id || c.id) !== classId);
        setSavedClasses(updated);
        localStorage.setItem('metafit_saved_classes', JSON.stringify(updated));
        toast.success("Removed from Wishlist");

        if (sessionStorage.getItem('userAccessToken') && itemToRemove) {
            try {
                await userApi.post('/wishlist/toggle', { classItem: itemToRemove });
            } catch (err) {
                console.error("Error removing wishlist item from backend:", err);
            }
        }
    };

    // Fetch already reviewed bookings for the logged-in user
    const loadReviewedBookingIds = async () => {
        try {
            const ids = await getUserReviewedBookingIds();
            setReviewedBookingIds(ids);
        } catch (e) {
            console.error("Error loading reviewed bookings:", e);
        }
    };

    const handleOpenReviewModal = (booking: UnifiedBooking) => {
        setReviewModalBooking(booking);
        setReviewRating(5);
        setReviewHoverRating(0);
        setReviewComment('');
    };

    const handleCloseReviewModal = () => {
        setReviewModalBooking(null);
        setReviewRating(5);
        setReviewHoverRating(0);
        setReviewComment('');
    };

    const handleReviewSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reviewModalBooking) return;

        if (!reviewComment.trim()) {
            toast.error("Please enter a short comment about your experience.");
            return;
        }

        setIsSubmittingReview(true);
        try {
            const bookingIdToSave = reviewModalBooking.id || (reviewModalBooking as any).bookingId || '';
            const res = await submitCustomerReview({
                name: profile.fullName || undefined,
                email: profile.email || undefined,
                phone: profile.phone || undefined,
                rating: reviewRating,
                comment: reviewComment.trim(),
                bookingId: bookingIdToSave,
                practitionerName: reviewModalBooking.instructor || undefined,
                serviceName: reviewModalBooking.title || reviewModalBooking.type || undefined,
                location: profile.location || undefined
            });

            if (res && res.success) {
                toast.success("Thank you! Your verified review has been submitted.");
                if (bookingIdToSave) {
                    setReviewedBookingIds(prev => [...prev, bookingIdToSave]);
                }
                handleCloseReviewModal();
            } else {
                toast.error(res?.message || "Failed to submit review");
            }
        } catch (err: any) {
            console.error("Review submission error:", err);
            toast.error(err?.response?.data?.message || "Failed to submit review. Please try again.");
        } finally {
            setIsSubmittingReview(false);
        }
    };

    // Update current time every minute
    useEffect(() => {
        loadReviewedBookingIds();
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    const calculateSessionDay = (startDate: string, endDate: string) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const current = new Date();

        if (start <= current) {
            const diffTime = Math.abs(current.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
            return {
                currentDay: Math.min(diffDays, totalDays),
                totalDays: totalDays,
                progressPercentage: totalDays > 0 ? Math.min((diffDays / totalDays) * 100, 100) : 0
            };
        }
        return { currentDay: 0, totalDays: 0, progressPercentage: 0 };
    };

    const convertTo24Hour = (time12h: string) => {
        if (!time12h) return '00:00';
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':');
        if (hours === '12') hours = '00';
        if (modifier === 'PM') hours = (parseInt(hours, 10) + 12).toString();
        return `${hours}:${minutes || '00'}`;
    };

    const getCurrentSessionLink = (googleMeetLinks: any[], classDuration: string) => {
        if (!googleMeetLinks || googleMeetLinks.length === 0) return null;
        const now = new Date();
        const durationMatch = classDuration?.match(/(\d+)/);
        const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 60;
        let bestLink = googleMeetLinks[0];

        for (const link of googleMeetLinks) {
            const timeStr = link.time || link.startTime || '00:00';
            const sessionTime24 = convertTo24Hour(timeStr);
            const sessionStart = new Date();
            const [hours, minutes] = sessionTime24.split(':');
            sessionStart.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            const sessionEnd = new Date(sessionStart);
            sessionEnd.setMinutes(sessionEnd.getMinutes() + durationMinutes);

            if (now >= sessionStart && now <= sessionEnd) {
                bestLink = link;
                break;
            } else if (now < sessionStart) {
                bestLink = link;
                break;
            }
        }
        return bestLink;
    };

    const getTimeStatus = (sessionTime: string, classDuration: string, startDate?: string, endDate?: string) => {
        const now = new Date();

        if (endDate && new Date(endDate) < now) {
            return 'Completed';
        }

        if (!sessionTime) {
            if (startDate && new Date(startDate) > now) return 'Upcoming';
            return 'Upcoming';
        }

        const sessionTime24 = convertTo24Hour(sessionTime);
        const [hours, minutes] = sessionTime24.split(':');
        const sessionStart = new Date();
        sessionStart.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        const durationMatch = classDuration?.match(/(\d+)/);
        const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 60;
        const sessionEnd = new Date(sessionStart);
        sessionEnd.setMinutes(sessionEnd.getMinutes() + durationMinutes);

        if (now >= sessionStart && now <= sessionEnd) {
            return 'Live';
        } else if (now < sessionStart) {
            return 'Upcoming';
        } else {
            return 'Completed';
        }
    };

    const handleFetchDetails = async () => {
        try {
            const cachedDataStr = sessionStorage.getItem(CACHE_KEY);
            let hasCache = false;
            if (cachedDataStr) {
                const cached = JSON.parse(cachedDataStr);
                if (Date.now() - cached.timestamp < CACHE_TIME) {
                    processFetchedData(cached.data);
                    hasCache = true;
                }
            }

            if (!hasCache) setLoading(true);
            const res = await fetchUserDetails() as userData;

            if (res.message === "Invalid token") {
                navigate("/login", { replace: true });
                return;
            }

            sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                timestamp: Date.now(),
                data: res
            }));

            processFetchedData(res);
        } catch (error) {
            console.error("[WARN]: Something Went Wrong!", error);
            setLoading(false);
        }
    };

    const processFetchedData = async (res: userData) => {
        if (!res || !res.user) {
            console.error("Invalid response from server:", res);
            toast.error(res.message || "Failed to load profile data. Please try again.");
            setLoading(false);
            return;
        }

        setServerData(res);

        const cleanFirst = res.user.firstname && res.user.firstname !== "undefined" ? res.user.firstname : "";
        const cleanLast = res.user.lastname && res.user.lastname !== "undefined" ? res.user.lastname : "";
        const cleanFullName = [cleanFirst, cleanLast].filter(Boolean).join(" ").trim() || "User";

        let cleanLocation = "";
        if (res.user.location && res.user.location !== "undefined" && res.user.location !== "null") {
            if (typeof res.user.location === "string") {
                cleanLocation = res.user.location.trim();
            } else if (typeof res.user.location === "object") {
                const loc: any = res.user.location;
                const parts = [loc.city, loc.state, loc.country].filter(Boolean);
                cleanLocation = parts.length > 0 ? parts.join(", ") : (loc.address || "");
            }
        }

        const cleanPhone = (res.user.phone && res.user.phone !== "undefined" && res.user.phone !== "null")
            ? String(res.user.phone).trim()
            : "";

        const cleanBio = (res.user.bio && res.user.bio !== "undefined" && res.user.bio !== "null")
            ? String(res.user.bio).trim()
            : "";

        setProfile({
            fullName: cleanFullName,
            email: res.user.useremail || "",
            profileImage: res.user.profileIcon && res.user.profileIcon !== "undefined" ? res.user.profileIcon : "",
            create_at: res.user.createAt || "",
            bio: cleanBio,
            location: cleanLocation,
            phone: cleanPhone,
            dob: res.user.dob && res.user.dob !== "undefined" ? res.user.dob : ""
        });

        // Sync wishlist from backend user data
        if (Array.isArray((res.user as any).wishlist)) {
            const dbWishlist = (res.user as any).wishlist;
            let localList: any[] = [];
            try {
                const raw = localStorage.getItem('metafit_saved_classes');
                if (raw) localList = JSON.parse(raw);
            } catch (e) {}

            const map = new Map();
            dbWishlist.forEach((c: any) => map.set(c._id || c.id, c));
            localList.forEach((c: any) => {
                const id = c._id || c.id;
                if (id && !map.has(id)) map.set(id, c);
            });
            const merged = Array.from(map.values());
            localStorage.setItem('metafit_saved_classes', JSON.stringify(merged));
            setSavedClasses(merged);
        }

        await fetchLabTestBookings(res.user.useremail, res);
    };

    const fetchLabTestBookings = async (email: string, res: userData) => {
        try {
            const { userApi } = require('../../config');
            const encodedEmail = encodeURIComponent(email);
            const response = await userApi.get(`/lab-test/bookings/user/${encodedEmail}`, {
                headers: { "Authorization": `Bearer ${sessionStorage.getItem("userAccessToken")}` }
            });
            const labTests = response.data?.bookings || response.data || [];
            buildUnifiedBookings(res, Array.isArray(labTests) ? labTests : []);
        } catch (error: any) {
            buildUnifiedBookings(res, []);
        }
    };

    const buildUnifiedBookings = (data: userData, labTests: labTestBookingData[]) => {
        const unified: UnifiedBooking[] = [];

        data.bookings?.forEach(b => {
            const cls = data.classes?.find(c => c._id === b.classId);

            if (!cls) {
                unified.push({
                    id: b.bookingId || b._id,
                    classId: b.classId,
                    type: 'Service',
                    title: b.classname || 'Wellness Service',
                    instructor: 'Metafit Expert',
                    date: b.bookingDate ? new Date(b.bookingDate) : new Date(b.createdAt),
                    duration: 'Flexible',
                    thumbnail: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80',
                    status: b.status === 'completed' ? 'Completed' : 'Upcoming',
                    progressPercentage: 0,
                    currentDay: 1,
                    totalDay: 1,
                    joinUrl: null,
                    createdAt: b.createdAt ? new Date(b.createdAt) : new Date()
                });
                return;
            }

            const sessionInfo = calculateSessionDay(cls.classStartDate || '', cls.classEndDate || '');
            const currentLink = getCurrentSessionLink(cls.googleMeetLinks || [], cls.classDuration || '');
            const timeString = currentLink?.time || currentLink?.startTime || '';
            const status = getTimeStatus(timeString, cls.classDuration || '', cls.classStartDate || '', cls.classEndDate || '') as any;

            unified.push({
                id: b.bookingId || b._id,
                classId: cls?._id,
                type: cls.classCategory === 'Meditation' ? 'Yoga' : (cls.classCategory as any) || 'Service',
                title: cls.classTitle || cls.serviceName || cls.consultationTitle || cls.treatmentName || cls.packageName || cls.testName || cls.title || cls.vendorName || `${cls.firstname || cls.firstName || ''} ${cls.lastname || cls.lastName || ''}`.trim() || b.classname || 'Wellness Class',
                instructor: (cls as any).instructorName || 'Metafit Expert',
                date: (b.selectedDates && b.selectedDates.length > 0) ? new Date(b.selectedDates[0]) : (b.bookingDate ? new Date(b.bookingDate) : (b.coverageStartDate ? new Date(b.coverageStartDate) : new Date(b.createdAt))),
                duration: cls.classDuration || 'Flexible',
                thumbnail: cls.classThumbnail || cls.thumbnail || cls.image || cls.photo || cls.icon || cls.profileIcon || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80',
                status: status,
                progressPercentage: sessionInfo.progressPercentage,
                currentDay: sessionInfo.currentDay,
                totalDay: sessionInfo.totalDays,
                joinUrl: currentLink?.url || null,
                createdAt: b.createdAt ? new Date(b.createdAt) : new Date()
            });
        });

        data.soundTherapyBookings?.forEach(sb => {
            unified.push({
                id: sb.bookingId || sb._id,
                type: 'Sound Therapy',
                title: 'Sound Healing & Meditation Therapy',
                instructor: 'Sound Therapy Master',
                date: new Date(sb.bookingDate),
                duration: sb.timeSlot || '60 mins',
                thumbnail: 'https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?auto=format&fit=crop&q=80',
                status: sb.status === 'completed' ? 'Completed' : 'Upcoming',
                progressPercentage: sb.status === 'completed' ? 100 : 0,
                currentDay: 1,
                totalDay: 1,
                joinUrl: null,
                createdAt: sb.createdAt ? new Date(sb.createdAt) : new Date(sb.bookingDate)
            });
        });

        data.dmitBookings?.forEach(db => {
            unified.push({
                id: db.bookingId || db._id,
                type: 'DMIT',
                title: 'DMIT Brain Mapping Assessment & Consultation',
                instructor: 'Cognitive Analyst',
                date: new Date(db.bookingDate),
                duration: db.timeSlot || '45 mins',
                thumbnail: 'https://images.unsplash.com/photo-1507413245164-6160d8298b31?auto=format&fit=crop&q=80',
                status: db.status === 'completed' ? 'Completed' : 'Upcoming',
                progressPercentage: db.status === 'completed' ? 100 : 0,
                currentDay: 1,
                totalDay: 1,
                joinUrl: null,
                createdAt: db.createdAt ? new Date(db.createdAt) : new Date(db.bookingDate)
            });
        });

        labTests?.forEach(lt => {
            unified.push({
                id: lt._id,
                type: 'Lab Test',
                title: `${lt.testName} (${lt.testCategory})`,
                instructor: 'Certified Phlebotomist',
                date: new Date(lt.createdAt),
                duration: 'Home Collection',
                thumbnail: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&q=80',
                status: lt.bookingStatus === 'completed' ? 'Completed' : (lt.bookingStatus === 'cancelled' ? 'Cancelled' : 'Upcoming'),
                progressPercentage: lt.bookingStatus === 'completed' ? 100 : 25,
                currentDay: 1,
                totalDay: 1,
                joinUrl: null,
                createdAt: new Date(lt.createdAt)
            });
        });

        data.consultationBookings?.forEach((cb: any) => {
            unified.push({
                id: cb._id,
                type: 'Consultation',
                title: cb.consultationTitle || 'Doctor Consultation',
                instructor: cb.doctorName || 'Ayurvedic Specialist',
                date: new Date(cb.bookingDate || cb.createdAt),
                duration: cb.timeSlot || '30 mins',
                thumbnail: 'https://images.unsplash.com/photo-1542844510-7e44a0e98033?auto=format&fit=crop&q=80',
                status: cb.status === 'completed' ? 'Completed' : 'Upcoming',
                progressPercentage: cb.status === 'completed' ? 100 : 0,
                currentDay: 1,
                totalDay: 1,
                joinUrl: cb.meetingLink || null,
                createdAt: new Date(cb.createdAt || Date.now())
            });
        });

        data.psychologyBookings?.forEach((pb: any) => {
            unified.push({
                id: pb._id,
                type: 'Consultation',
                title: pb.sessionTitle || 'Psychology Session',
                instructor: pb.doctorName || 'Licensed Psychologist',
                date: new Date(pb.bookingDate || pb.createdAt),
                duration: pb.timeSlot || '45 mins',
                thumbnail: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&q=80',
                status: pb.status === 'completed' ? 'Completed' : 'Upcoming',
                progressPercentage: pb.status === 'completed' ? 100 : 0,
                currentDay: 1,
                totalDay: 1,
                joinUrl: pb.meetingLink || null,
                createdAt: new Date(pb.createdAt || Date.now())
            });
        });

        data.treatmentBookings?.forEach((tb: any) => {
            unified.push({
                id: tb._id,
                type: 'Service',
                title: tb.treatmentName || 'Wellness Treatment',
                instructor: tb.centerName || 'Wellness Retreat Specialist',
                date: new Date(tb.bookingDate || tb.createdAt),
                duration: 'Multi-day',
                thumbnail: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80',
                status: tb.status === 'completed' ? 'Completed' : 'Upcoming',
                progressPercentage: tb.status === 'completed' ? 100 : 0,
                currentDay: 1,
                totalDay: 1,
                joinUrl: null,
                createdAt: new Date(tb.createdAt || Date.now())
            });
        });

        setUnifiedBookings(unified);
        setLoading(false);
    };

    useEffect(() => {
        handleFetchDetails();
    }, []);

    const filteredBookings = useMemo(() => {
        let result = [...unifiedBookings];

        if (activeTab === 'Upcoming Sessions') {
            result = result.filter(b => b.status === 'Upcoming' || b.status === 'Live');
        } else if (activeTab === 'Past Classes') {
            result = result.filter(b => b.status === 'Completed');
        } else if (activeTab === 'My Classes') {
            result = result.filter(b => b.type === 'Yoga' || b.type?.toLowerCase().includes('class'));
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(b =>
                b.title.toLowerCase().includes(q) ||
                b.instructor.toLowerCase().includes(q) ||
                b.type.toLowerCase().includes(q)
            );
        }

        if (categoryFilter !== 'All Categories') {
            result = result.filter(b => b.type.toLowerCase() === categoryFilter.toLowerCase());
        }

        if (statusFilter !== 'All Status') {
            result = result.filter(b => b.status === statusFilter);
        }

        if (dateFilter !== 'All Dates') {
            const selectedDate = new Date(dateFilter).toDateString();
            result = result.filter(b => b.date.toDateString() === selectedDate);
        }

        result.sort((a, b) => {
            if (activeTab === 'Past Classes') {
                return b.date.getTime() - a.date.getTime();
            }
            return a.date.getTime() - b.date.getTime();
        });
        return result;
    }, [unifiedBookings, activeTab, searchQuery, categoryFilter, statusFilter, dateFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchQuery, categoryFilter, statusFilter, dateFilter]);

    if (loading && !serverData) {
        return (
            <div className="min-h-screen bg-[#F6F4EE] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#134e4a]"></div>
            </div>
        );
    }

    if (!serverData) {
        return (
            <div className="min-h-screen bg-[#F6F4EE] flex items-center justify-center">
                <p className="text-gray-500">No data available.</p>
            </div>
        );
    }

    const userInitials = ((profile.fullName?.[0] || "") + (profile.fullName?.split(' ')?.[1]?.[0] || "")).toUpperCase() || "KP";

    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.removeItem("token");
        localStorage.removeItem("userAccessToken");
        localStorage.removeItem("mentorAccessToken");
        localStorage.removeItem("adminAccessToken");
        localStorage.removeItem("user");
        window.location.href = '/login';
    };

    const formatDateOfBirth = (dob?: string) => {
        if (!dob || dob === "undefined" || dob === "null") return "Not provided";
        try {
            let dateObj: Date | null = null;
            if (dob.includes('-')) {
                const parts = dob.split('-');
                if (parts[0].length === 4) {
                    dateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                } else if (parts[2].length === 4) {
                    dateObj = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                }
            }
            if (!dateObj || isNaN(dateObj.getTime())) {
                dateObj = new Date(dob);
            }
            if (dateObj && !isNaN(dateObj.getTime())) {
                return dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            return dob;
        } catch {
            return dob;
        }
    };

    const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
    const paginatedBookings = filteredBookings.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const navItems = [
        { id: 'My Profile', label: 'My Profile', icon: User },
        { id: 'My Bookings', label: 'My Bookings', icon: Calendar },
        { id: 'My Classes', label: 'My Classes', icon: Video },
        { id: 'Upcoming Sessions', label: 'Upcoming Sessions', icon: Clock },
        { id: 'Past Classes', label: 'Past Classes', icon: FileText },
        { id: 'Saved / Wishlist', label: 'Saved / Wishlist', icon: Heart },
    ];

    return (
        <div className="min-h-screen bg-[#F6F4EE] text-gray-800 font-sans pb-16 relative overflow-x-hidden">
            <Toaster />

            {/* Subtle botanical background accents */}
            <div className="fixed -top-16 -left-16 w-80 h-80 bg-emerald-100/30 rounded-full blur-3xl pointer-events-none" />
            <div className="fixed top-1/2 -right-20 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl pointer-events-none" />

            <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 relative z-10">

                {/* ========================================================
                    TOP HEADER BANNER CARD (Matches Screenshot)
                   ======================================================== */}
                <div className="bg-white rounded-3xl border border-gray-100/80 shadow-[0_4px_24px_rgba(0,0,0,0.03)] overflow-hidden relative p-6 sm:p-8 mb-6">
                    {/* Scenic meditation background image on the right */}
                    <div
                        className="hidden md:block absolute right-0 top-0 bottom-0 w-[45%] bg-cover bg-center pointer-events-none opacity-90 [mask-image:linear-gradient(to_left,black_60%,transparent_100%)]"
                        style={{
                            backgroundImage: `url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=80')`
                        }}
                    />

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                        {/* Avatar & User Details */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6">
                            {/* Avatar with status dot */}
                            <div className="relative shrink-0">
                                {profile.profileImage ? (
                                    <img
                                        src={profile.profileImage}
                                        alt={profile.fullName}
                                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-white shadow-md"
                                    />
                                ) : (
                                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#E5F3F0] flex items-center justify-center border-4 border-white shadow-md">
                                        <span className="text-[#134e4a] text-2xl sm:text-3xl font-extrabold tracking-wide">
                                            {userInitials}
                                        </span>
                                    </div>
                                )}
                                {/* Teal/Green Online Status Badge */}
                                <span className="w-5 h-5 bg-[#00BA88] border-2 border-white rounded-full absolute bottom-1 right-1.5 shadow-sm" />
                            </div>

                            {/* Name, Role & Info */}
                            <div className="space-y-1.5 max-w-xl">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                                        {profile.fullName || "User"}
                                    </h1>
                                    <CheckCircle2 className="w-5 h-5 text-teal-600 fill-teal-100 shrink-0" />
                                </div>

                                <div className="flex flex-wrap items-center gap-y-1.5 gap-x-4 text-xs text-gray-500 pt-0.5">
                                    {profile.email && (
                                        <div className="flex items-center gap-1.5">
                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                            <span>{profile.email}</span>
                                        </div>
                                    )}
                                    {profile.phone && (
                                        <div className="flex items-center gap-1.5">
                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                            <span>{profile.phone}</span>
                                        </div>
                                    )}
                                    {profile.location && (
                                        <div className="flex items-center gap-1.5">
                                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                            <span>{profile.location}</span>
                                        </div>
                                    )}
                                </div>

                                {profile.bio ? (
                                    <p className="text-xs sm:text-sm italic text-gray-500 pt-1 font-serif leading-relaxed">
                                        "{profile.bio}"
                                    </p>
                                ) : null}
                            </div>
                        </div>

                    </div>
                </div>

                {/* ========================================================
                    METRICS / STATS ROW (4 Cards)
                   ======================================================== */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8">
                    {/* Card 1: Total Classes */}
                    <button
                        onClick={() => setActiveTab('My Classes')}
                        className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] flex items-center justify-between hover:shadow-md transition-all group text-left cursor-pointer"
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                <Users className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-none">
                                    {serverData?.classes?.length || 0}
                                </div>
                                <div className="text-xs text-gray-500 font-medium mt-1">Total Classes</div>
                            </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                    </button>

                    {/* Card 2: Total Bookings */}
                    <button
                        onClick={() => setActiveTab('My Bookings')}
                        className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] flex items-center justify-between hover:shadow-md transition-all group text-left cursor-pointer"
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-none">
                                    {unifiedBookings.length}
                                </div>
                                <div className="text-xs text-gray-500 font-medium mt-1">Total Bookings</div>
                            </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                    </button>

                    {/* Card 3: Meta Coins */}
                    <div
                        className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] flex items-center justify-between"
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                                <Coins className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-none">
                                    {unifiedBookings.length * 5}
                                </div>
                                <div className="text-xs text-gray-500 font-medium mt-1">Meta Coins</div>
                            </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>

                    {/* Card 4: Reviews */}
                    <button
                        onClick={() => setActiveTab('Past Classes')}
                        className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] flex items-center justify-between hover:shadow-md transition-all group text-left cursor-pointer"
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
                                <Star className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-none">
                                    {reviewedBookingIds.length || 0}
                                </div>
                                <div className="text-xs text-gray-500 font-medium mt-1">Reviews</div>
                            </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-rose-500 group-hover:translate-x-0.5 transition-all" />
                    </button>
                </div>

                {/* ========================================================
                    MAIN TWO-COLUMN WORKSPACE
                   ======================================================== */}
                <div className="flex flex-col lg:flex-row gap-6 items-start">

                    {/* ----------------------------------------------------
                        LEFT SIDEBAR NAVIGATION MENU (Matches Screenshot)
                       ---------------------------------------------------- */}
                    <aside className="w-full lg:w-64 shrink-0 bg-white rounded-2xl p-3 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] space-y-1">
                        {navItems.map((item) => {
                            const IconComponent = item.icon;
                            const isActive = activeTab === item.id;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                                        isActive
                                            ? 'bg-[#134e4a] text-white shadow-sm'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                >
                                    <IconComponent className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                                    <span>{item.label}</span>
                                    {item.id === 'Saved / Wishlist' && savedClasses.length > 0 && (
                                        <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                                            isActive ? 'bg-white text-emerald-800' : 'bg-rose-500 text-white'
                                        }`}>
                                            {savedClasses.length}
                                        </span>
                                    )}
                                </button>
                            );
                        })}

                        <hr className="my-2.5 border-gray-100" />

                        {/* Settings Button */}
                        <button
                            onClick={() => navigate('/profile/user-profile-settings')}
                            className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                            <Settings className="w-4 h-4 text-gray-500" />
                            <span>Settings</span>
                        </button>

                        {/* Help & Support Button */}
                        <button
                            onClick={() => setIsSupportModalOpen(true)}
                            className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                            <HelpCircle className="w-4 h-4 text-gray-500" />
                            <span>Help & Support</span>
                        </button>

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                            <LogOut className="w-4 h-4 text-red-500" />
                            <span>Logout</span>
                        </button>
                    </aside>

                    {/* ----------------------------------------------------
                        RIGHT CONTENT AREA
                       ---------------------------------------------------- */}
                    <main className="flex-1 min-w-0 w-full">

                        {/* ================================================
                            TAB 1: MY PROFILE (EXACT SCREENSHOT VIEW)
                           ================================================ */}
                        {activeTab === 'My Profile' && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                                {/* Left Card: Personal Information (2 Cols) */}
                                <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
                                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                                                <User className="w-4 h-4" />
                                            </div>
                                            <h2 className="text-lg font-bold text-gray-900">Personal Information</h2>
                                        </div>
                                        <button
                                            onClick={() => navigate('/profile/user-profile-settings')}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors shadow-sm cursor-pointer"
                                        >
                                            <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                                            <span>Edit</span>
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Full Name */}
                                        <div className="flex items-center py-2.5 border-b border-gray-50">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mr-4">
                                                <User className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-gray-500 w-36 font-normal">Full Name</span>
                                            <span className="text-sm font-semibold text-gray-900 flex-1">
                                                {profile.fullName || ""}
                                            </span>
                                        </div>

                                        {/* Email Address */}
                                        <div className="flex items-center py-2.5 border-b border-gray-50">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mr-4">
                                                <Mail className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-gray-500 w-36 font-normal">Email Address</span>
                                            <span className="text-sm font-semibold text-gray-900 flex-1 truncate">
                                                {profile.email || ""}
                                            </span>
                                        </div>

                                        {/* Phone Number */}
                                        <div className="flex items-center py-2.5 border-b border-gray-50">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mr-4">
                                                <Phone className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-gray-500 w-36 font-normal">Phone Number</span>
                                            <span className="text-sm font-semibold text-gray-900 flex-1">
                                                {profile.phone || ""}
                                            </span>
                                        </div>

                                        {/* Date of Birth */}
                                        <div className="flex items-center py-2.5 border-b border-gray-50">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mr-4">
                                                <Calendar className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-gray-500 w-36 font-normal">Date of Birth</span>
                                            <span className="text-sm font-semibold text-gray-900 flex-1">
                                                {profile.dob ? formatDateOfBirth(profile.dob) : ""}
                                            </span>
                                        </div>

                                        {/* Location */}
                                        <div className="flex items-center py-2.5 border-b border-gray-50">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mr-4">
                                                <MapPin className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-gray-500 w-36 font-normal">Location</span>
                                            <span className="text-sm font-semibold text-gray-900 flex-1">
                                                {profile.location || ""}
                                            </span>
                                        </div>

                                        {/* Bio */}
                                        <div className="flex items-start py-2.5">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mr-4 mt-0.5">
                                                <FileText className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-gray-500 w-36 font-normal pt-0.5">Bio</span>
                                            <span className="text-sm font-normal text-gray-700 flex-1 leading-relaxed">
                                                {profile.bio || ""}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Status, Goals, Social Links (1 Col) */}
                                <div className="space-y-6">

                                    {/* Account Status Card */}
                                    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
                                        <div className="flex items-center gap-2 mb-4">
                                            <ShieldCheck className="w-5 h-5 text-teal-700" />
                                            <h3 className="text-sm font-bold text-gray-900">Account Status</h3>
                                        </div>
                                        <div className="bg-emerald-50/70 border border-emerald-100 rounded-xl p-3.5 flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm">
                                                <CheckCircle2 className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold text-gray-900">Verified Partner</div>
                                                <div className="text-[11px] text-gray-500 mt-0.5">Your account is active and verified.</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* My Goals Card */}
                                    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Target className="w-5 h-5 text-teal-700" />
                                                <h3 className="text-sm font-bold text-gray-900">My Goals</h3>
                                            </div>
                                            <button
                                                onClick={() => setIsGoalsModalOpen(true)}
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 px-2.5 py-1 rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
                                            >
                                                <Edit3 className="w-3 h-3 text-gray-500" />
                                                <span>Edit</span>
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {goals.map((g, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 border shadow-2xs ${
                                                        g.color === 'emerald' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                                                        g.color === 'blue' ? 'bg-blue-50 text-blue-800 border-blue-200' :
                                                        g.color === 'amber' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                                                        'bg-purple-50 text-purple-800 border-purple-200'
                                                    }`}
                                                >
                                                    <span>{g.emoji}</span> {g.text}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* ================================================
                            TAB 2: BOOKINGS / CLASSES / UPCOMING / PAST TABS
                            (PRESERVES 100% OF EXISTING BOOKING FEATURES)
                           ================================================ */}
                        {(activeTab === 'My Bookings' || activeTab === 'My Classes' || activeTab === 'Upcoming Sessions' || activeTab === 'Past Classes') && (
                            <div className="space-y-6">
                                {/* Header & Filters Bar */}
                                <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] space-y-4">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        {/* Sub-tabs */}
                                        <div className="flex gap-2 p-1 bg-gray-100/70 rounded-xl overflow-x-auto hide-scrollbar">
                                            {['My Bookings', 'Upcoming Sessions', 'Past Classes'].map((tab) => (
                                                <button
                                                    key={tab}
                                                    onClick={() => setActiveTab(tab)}
                                                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                                                        activeTab === tab
                                                            ? 'bg-white text-[#134e4a] shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-800'
                                                    }`}
                                                >
                                                    {tab}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Search Box */}
                                        <div className="relative flex-1 md:max-w-xs">
                                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                            <input
                                                type="text"
                                                placeholder="Search classes or instructors..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-xs rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#134e4a]/20 focus:border-[#134e4a] transition-all"
                                            />
                                        </div>
                                    </div>

                                    {/* Category, Status & Date Filters */}
                                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
                                        <select
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value)}
                                            className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-medium rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
                                        >
                                            <option>All Categories</option>
                                            <option>Yoga</option>
                                            <option>Consultation</option>
                                            <option>Sound Therapy</option>
                                            <option>DMIT</option>
                                            <option>Lab Test</option>
                                        </select>

                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-medium rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
                                        >
                                            <option>All Status</option>
                                            <option>Live</option>
                                            <option>Upcoming</option>
                                            <option>Completed</option>
                                        </select>

                                        <input
                                            type="date"
                                            value={dateFilter === 'All Dates' ? '' : dateFilter}
                                            onChange={(e) => setDateFilter(e.target.value || 'All Dates')}
                                            className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-medium rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
                                        />

                                        {(searchQuery || categoryFilter !== 'All Categories' || statusFilter !== 'All Status' || dateFilter !== 'All Dates') && (
                                            <button
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    setCategoryFilter('All Categories');
                                                    setStatusFilter('All Status');
                                                    setDateFilter('All Dates');
                                                }}
                                                className="text-xs text-red-600 hover:text-red-700 font-semibold px-2 py-1 ml-auto"
                                            >
                                                Reset Filters
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Compact Bookings Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                                    {filteredBookings.length === 0 ? (
                                        <div className="col-span-full text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
                                            <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                            <h3 className="text-lg font-bold text-gray-700 mb-1">No Bookings Found</h3>
                                            <p className="text-gray-500 text-sm">We couldn't find any sessions matching your selected filters.</p>
                                        </div>
                                    ) : (
                                        paginatedBookings.map((booking) => (
                                            <div
                                                key={booking.id}
                                                className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] flex flex-col group hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                                            >
                                                <div className="relative h-32 overflow-hidden bg-gray-100">
                                                    <img
                                                        src={booking.thumbnail}
                                                        alt={booking.title}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                    />
                                                    <div className="absolute top-2 left-2">
                                                        <span className="bg-white/95 backdrop-blur text-gray-800 text-[10px] font-bold px-2 py-1 rounded-md shadow-sm">
                                                            {booking.type}
                                                        </span>
                                                    </div>
                                                    <div className="absolute top-2 right-2">
                                                        <span
                                                            className={`text-[9px] font-bold px-2 py-1 rounded-md shadow-sm flex items-center gap-1 ${
                                                                booking.status === 'Live'
                                                                    ? 'bg-orange-500 text-white'
                                                                    : booking.status === 'Completed'
                                                                    ? 'bg-green-500 text-white'
                                                                    : booking.status === 'Cancelled'
                                                                    ? 'bg-red-500 text-white'
                                                                    : 'bg-gray-900 text-white'
                                                            }`}
                                                        >
                                                            {booking.status === 'Live' && (
                                                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                                                            )}
                                                            {booking.status}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="p-3.5 flex flex-col flex-1">
                                                    <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-1 mb-1.5">
                                                        {booking.title}
                                                    </h3>

                                                    <div className="flex items-center gap-1.5 mb-3">
                                                        <User className="w-3.5 h-3.5 text-[#134e4a] shrink-0" />
                                                        <span className="text-xs font-medium text-gray-600 truncate">
                                                            {booking.instructor}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-3 bg-gray-50 p-2 rounded-lg">
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            <span>
                                                                {booking.date.toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: '2-digit'
                                                                })}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Clock className="w-3.5 h-3.5" />
                                                            <span>{booking.duration}</span>
                                                        </div>
                                                    </div>

                                                    <div className="mt-auto pt-2.5 border-t border-gray-100">
                                                        <div className="flex justify-between text-[10px] font-bold mb-1">
                                                            <span className="text-gray-400">
                                                                Progress: {booking.currentDay}/{booking.totalDay}
                                                            </span>
                                                            <span className="text-[#134e4a]">
                                                                {Math.round(booking.progressPercentage)}%
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-gray-100 rounded-full h-1 mb-3 overflow-hidden">
                                                            <div
                                                                className="bg-[#134e4a] h-full rounded-full transition-all duration-1000"
                                                                style={{ width: `${booking.progressPercentage}%` }}
                                                            />
                                                        </div>

                                                        {booking.status === 'Live' ? (
                                                            <button
                                                                onClick={() => window.open(booking.joinUrl || '', '_blank')}
                                                                className="w-full py-2 bg-[#134e4a] hover:bg-[#0f3d38] text-white text-xs font-bold rounded-lg transition-colors shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                                                            >
                                                                Join Live <ArrowRight className="w-3 h-3" />
                                                            </button>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => navigate('/class-details/' + (booking.classId || booking.id))}
                                                                    className="flex-1 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-bold rounded-lg transition-colors shadow-sm flex items-center justify-center gap-1 cursor-pointer"
                                                                >
                                                                    View Details <ArrowRight className="w-3 h-3" />
                                                                </button>
                                                                {booking.status === 'Completed' && (
                                                                    reviewedBookingIds.includes(booking.id || '') ? (
                                                                        <span
                                                                            className="px-2.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-semibold rounded-lg flex items-center gap-1 shrink-0"
                                                                            title="You reviewed this session"
                                                                        >
                                                                            <Star className="w-3 h-3 fill-emerald-600 text-emerald-600" /> Reviewed
                                                                        </span>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenReviewModal(booking)}
                                                                            className="px-2.5 py-1.5 bg-[#E6A93F]/15 hover:bg-[#E6A93F]/25 text-[#9A7122] border border-[#E6A93F]/40 text-[11px] font-bold rounded-lg transition-colors shadow-sm flex items-center gap-1 shrink-0 cursor-pointer"
                                                                            title="Review this session"
                                                                        >
                                                                            <Star className="w-3 h-3 fill-[#E6A93F] text-[#E6A93F]" /> Review
                                                                        </button>
                                                                    )
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Pagination */}
                                {filteredBookings.length > 0 && totalPages > 1 && (
                                    <div className="flex items-center justify-between text-xs text-gray-500 font-medium px-1 pt-2">
                                        <div className="flex gap-1.5 flex-wrap">
                                            <button
                                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                            >
                                                <ChevronLeft className="w-3 h-3" />
                                            </button>

                                            {Array.from({ length: totalPages }).map((_, idx) => {
                                                const page = idx + 1;
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => setCurrentPage(page)}
                                                        className={`w-7 h-7 flex items-center justify-center border rounded-md cursor-pointer ${
                                                            currentPage === page
                                                                ? 'border-[#134e4a] bg-[#134e4a] text-white font-bold'
                                                                : 'border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            })}

                                            <button
                                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                            >
                                                <ChevronRight className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <span>
                                            Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredBookings.length)} of {filteredBookings.length}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ================================================
                            TAB 3: SAVED / WISHLIST
                           ================================================ */}
                        {activeTab === 'Saved / Wishlist' && (
                            <div className="space-y-6">
                                {/* Header Bar */}
                                <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
                                            <Heart className="w-5 h-5 fill-current" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">
                                                Saved Classes & Wishlist ({savedClasses.length})
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                Classes you liked and saved to book anytime
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => navigate('/classes')}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#134e4a] text-white text-xs font-semibold rounded-xl hover:bg-[#0f3d38] transition-colors shadow-sm cursor-pointer"
                                    >
                                        <span>Browse More Classes</span>
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                {savedClasses.length === 0 ? (
                                    <div className="bg-white rounded-2xl p-12 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] text-center space-y-4">
                                        <div className="w-16 h-16 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mx-auto">
                                            <Heart className="w-8 h-8" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">Your Saved Wishlist is Empty</h3>
                                        <p className="text-sm text-gray-500 max-w-md mx-auto">
                                            You haven't liked or saved any classes yet. Browse our live sessions, hit the heart icon on any class you like, and they will appear right here!
                                        </p>
                                        <button
                                            onClick={() => navigate('/classes')}
                                            className="px-6 py-2.5 bg-[#134e4a] text-white text-sm font-semibold rounded-xl hover:bg-[#0f3d38] transition-colors shadow-sm cursor-pointer"
                                        >
                                            Explore Classes
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                        {savedClasses.map((item: any) => {
                                            const classId = item._id || item.id;
                                            const detailUrl = `/class-details/${btoa(btoa(classId))}`;
                                            const formattedDate = item.classStartDate
                                                ? new Date(item.classStartDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                                                : 'Upcoming';

                                            return (
                                                <div
                                                    key={classId}
                                                    className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] hover:shadow-md transition-all overflow-hidden flex flex-col group"
                                                >
                                                    {/* Card Image */}
                                                    <div className="relative h-40 bg-gray-100 overflow-hidden">
                                                        {item.classThumbnail ? (
                                                            <img
                                                                src={item.classThumbnail}
                                                                alt={item.classTitle}
                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                                                                {item.classTitle || "Yoga Class"}
                                                            </div>
                                                        )}
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                                                        {/* Category Pill */}
                                                        {item.classCategory && (
                                                            <span className="absolute bottom-2.5 left-2.5 bg-white/90 backdrop-blur-md text-teal-800 text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-md shadow-sm">
                                                                {item.classCategory}
                                                            </span>
                                                        )}

                                                        {/* Remove from Wishlist Button */}
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleRemoveFromWishlist(classId);
                                                            }}
                                                            className="absolute top-2.5 right-2.5 p-2 rounded-full bg-white/90 hover:bg-rose-50 text-rose-500 hover:text-rose-600 shadow-md backdrop-blur-sm transition-all hover:scale-110 cursor-pointer"
                                                            title="Remove from Saved / Wishlist"
                                                        >
                                                            <Heart className="w-4 h-4 fill-current text-rose-500" />
                                                        </button>
                                                    </div>

                                                    {/* Card Content */}
                                                    <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                                                        <div>
                                                            <div className="flex items-start justify-between gap-2">
                                                                <h4 className="text-base font-bold text-gray-900 group-hover:text-teal-700 transition-colors line-clamp-1">
                                                                    {item.classTitle}
                                                                </h4>
                                                                {item.mentorRating && (
                                                                    <div className="flex items-center gap-1 text-xs text-amber-500 font-semibold shrink-0">
                                                                        <Star className="w-3.5 h-3.5 fill-current" />
                                                                        <span>{Number(item.mentorRating).toFixed(1)}</span>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Instructor */}
                                                            <div className="flex items-center gap-2.5 mt-2.5">
                                                                <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
                                                                    {item.instructorThumbnail ? (
                                                                        <img src={item.instructorThumbnail} alt={item.instructorName} className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        (item.instructorName || 'I').charAt(0).toUpperCase()
                                                                    )}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="text-xs font-semibold text-gray-800 truncate">
                                                                        {item.instructorName || "Instructor"}
                                                                    </p>
                                                                    <p className="text-[10px] text-gray-400">Instructor</p>
                                                                </div>
                                                            </div>

                                                            {/* Date & Duration */}
                                                            <div className="flex items-center justify-between text-[11px] text-gray-500 pt-2 border-t border-gray-50 mt-3">
                                                                <div className="flex items-center gap-1">
                                                                    <Calendar className="w-3 h-3 text-teal-500" />
                                                                    <span>{formattedDate}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <Clock className="w-3 h-3 text-teal-500" />
                                                                    <span>
                                                                        {item.classDuration
                                                                            ? String(item.classDuration).toLowerCase().includes('min')
                                                                                ? item.classDuration
                                                                                : `${item.classDuration} mins`
                                                                            : '45 mins'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Price & Book Now Button */}
                                                        <div className="pt-2">
                                                            <div className="text-xs font-bold text-gray-900 mb-2">
                                                                {item.purchaseStatus === 'Free Tier' || (item.classPrice && parseInt(item.classPrice.session) === 0) ? (
                                                                    <span className="text-emerald-600 font-extrabold">FREE</span>
                                                                ) : item.classPrice?.session ? (
                                                                    <span>₹{item.classPrice.session} <span className="text-[10px] text-gray-400 font-normal">/ Session</span></span>
                                                                ) : (
                                                                    <span className="text-gray-700">₹600 / Session</span>
                                                                )}
                                                            </div>

                                                            <Link
                                                                to={detailUrl}
                                                                className="w-full py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm hover:shadow transition-all"
                                                            >
                                                                <span>BOOK NOW</span>
                                                                <ArrowRight className="w-3.5 h-3.5" />
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                    </main>
                </div>

                {/* ========================================================
                    INVITE FRIENDS BANNER (Preserved at bottom)
                   ======================================================== */}
                <div className="mt-10 bg-gradient-to-r from-[#e6f3f2] to-emerald-50 border border-[#cce7e5] p-6 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm relative overflow-hidden">
                    <div className="flex items-center gap-4 z-10">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0">
                            <Gift className="w-6 h-6 text-[#134e4a]" />
                        </div>
                        <div>
                            <h4 className="text-[#08312f] text-lg font-bold">Invite Friends & Earn Rewards</h4>
                            <p className="text-sm text-[#08312f]/80 font-medium">
                                Invite your friends and earn <span className="font-bold">100 Meta Coins</span> for every successful referral!
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (navigator.clipboard) {
                                navigator.clipboard.writeText(window.location.origin);
                                toast.success("Referral link copied to clipboard!");
                            }
                        }}
                        className="bg-[#134e4a] hover:bg-[#0f3d38] text-white text-sm font-bold px-6 py-2.5 rounded-xl transition-colors shadow-sm whitespace-nowrap z-10 flex items-center gap-2 cursor-pointer"
                    >
                        Invite Now <ArrowRight className="w-4 h-4" />
                    </button>
                    <div className="absolute -right-10 -bottom-10 opacity-20 pointer-events-none">
                        <Gift className="w-48 h-48 text-[#134e4a]" />
                    </div>
                </div>

            </div>

            {/* ========================================================
                CUSTOMER REVIEW MODAL (Completed Bookings)
               ======================================================== */}
            {reviewModalBooking && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative">
                        <button
                            type="button"
                            onClick={handleCloseReviewModal}
                            className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="mb-6">
                            <span className="text-[11px] font-mono tracking-widest uppercase text-[#9A7122] font-semibold bg-[#E6A93F]/15 px-3 py-1 rounded-full">
                                Verified Review
                            </span>
                            <h3 className="text-xl font-bold text-gray-900 mt-2 font-serif">
                                How was your experience?
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                                {reviewModalBooking.title} with <span className="font-semibold text-gray-700">{reviewModalBooking.instructor}</span>
                            </p>
                        </div>

                        <form onSubmit={handleReviewSubmit}>
                            <div className="mb-6">
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                                    Rating (1 to 5 Stars)
                                </label>
                                <div className="flex items-center gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => {
                                        const isFilled = (reviewHoverRating || reviewRating) >= star;
                                        return (
                                            <button
                                                key={star}
                                                type="button"
                                                onMouseEnter={() => setReviewHoverRating(star)}
                                                onMouseLeave={() => setReviewHoverRating(0)}
                                                onClick={() => setReviewRating(star)}
                                                className="p-1.5 focus:outline-none transition-transform hover:scale-110 cursor-pointer"
                                            >
                                                <Star
                                                    className={`w-7 h-7 ${
                                                        isFilled
                                                            ? 'fill-[#E6A93F] text-[#E6A93F]'
                                                            : 'text-gray-200 fill-gray-100'
                                                    }`}
                                                />
                                            </button>
                                        );
                                    })}
                                    <span className="text-sm font-bold text-[#15302B] ml-2">
                                        {reviewHoverRating || reviewRating} / 5
                                    </span>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                                    Your Feedback
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder="Share your thoughts about the instructor, pacing, or key takeaways..."
                                    className="w-full bg-[#FAF9F5] border border-gray-200 rounded-xl p-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#134e4a] resize-none"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={handleCloseReviewModal}
                                    disabled={isSubmittingReview}
                                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmittingReview}
                                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-[#134e4a] hover:bg-[#0f3d38] transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                                >
                                    {isSubmittingReview ? (
                                        <>
                                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        "Submit Review"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========================================================
                EDIT GOALS MODAL
               ======================================================== */}
            {isGoalsModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 relative">
                        <button
                            onClick={() => setIsGoalsModalOpen(false)}
                            className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Manage My Goals</h3>
                        <p className="text-xs text-gray-500 mb-4">Add your personal health and teaching aspirations.</p>

                        <div className="space-y-2 mb-4">
                            {goals.map((g, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl text-xs font-semibold">
                                    <span>{g.emoji} {g.text}</span>
                                    <button
                                        onClick={() => setGoals(prev => prev.filter((_, i) => i !== idx))}
                                        className="text-red-500 hover:text-red-700 text-xs px-2 py-0.5 cursor-pointer"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="E.g. Daily Meditation"
                                value={newGoalText}
                                onChange={(e) => setNewGoalText(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-[#134e4a]"
                            />
                            <button
                                onClick={() => {
                                    if (newGoalText.trim()) {
                                        setGoals(prev => [...prev, { emoji: '✨', text: newGoalText.trim(), color: 'emerald' }]);
                                        setNewGoalText('');
                                    }
                                }}
                                className="px-4 py-2 bg-[#134e4a] text-white text-xs font-bold rounded-xl hover:bg-[#0f3d38] cursor-pointer"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========================================================
                EDIT SOCIAL LINKS MODAL
               ======================================================== */}
            {isSocialModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 relative">
                        <button
                            onClick={() => setIsSocialModalOpen(false)}
                            className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Social Profiles</h3>
                        <p className="text-xs text-gray-500 mb-4">Add or update your social media links.</p>

                        <div className="space-y-3 mb-5">
                            <div>
                                <label className="text-xs font-semibold text-gray-700 block mb-1">Instagram URL</label>
                                <input
                                    type="text"
                                    value={socialLinks.instagram}
                                    onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs"
                                    placeholder="https://instagram.com/yourprofile"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-700 block mb-1">YouTube URL</label>
                                <input
                                    type="text"
                                    value={socialLinks.youtube}
                                    onChange={(e) => setSocialLinks(prev => ({ ...prev, youtube: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs"
                                    placeholder="https://youtube.com/@channel"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-700 block mb-1">Facebook URL</label>
                                <input
                                    type="text"
                                    value={socialLinks.facebook}
                                    onChange={(e) => setSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs"
                                    placeholder="https://facebook.com/profile"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-700 block mb-1">LinkedIn URL</label>
                                <input
                                    type="text"
                                    value={socialLinks.linkedin}
                                    onChange={(e) => setSocialLinks(prev => ({ ...prev, linkedin: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs"
                                    placeholder="https://linkedin.com/in/profile"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={() => {
                                    setIsSocialModalOpen(false);
                                    toast.success("Social links updated!");
                                }}
                                className="px-5 py-2.5 bg-[#134e4a] text-white text-xs font-bold rounded-xl hover:bg-[#0f3d38] cursor-pointer"
                            >
                                Save Links
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========================================================
                HELP & SUPPORT MODAL
               ======================================================== */}
            {isSupportModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 relative">
                        <button
                            onClick={() => setIsSupportModalOpen(false)}
                            className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Help & Support</h3>
                        <p className="text-xs text-gray-500 mb-5">Have any questions or need assistance with your bookings?</p>

                        <div className="space-y-3 mb-6">
                            <a
                                href="mailto:cto.metafit@gmail.com"
                                className="flex items-center gap-3 p-3.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group cursor-pointer"
                            >
                                <div className="w-9 h-9 rounded-lg bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center text-teal-700 transition-colors shrink-0">
                                    <Mail className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-gray-900">Email Support</div>
                                    <div className="text-xs text-gray-600 group-hover:text-teal-700 transition-colors">cto.metafit@gmail.com</div>
                                </div>
                            </a>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setIsSupportModalOpen(false);
                                    navigate('/contact');
                                }}
                                className="px-4 py-2 border border-gray-200 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-50 cursor-pointer"
                            >
                                Contact Page
                            </button>
                            <button
                                onClick={() => setIsSupportModalOpen(false)}
                                className="px-5 py-2 bg-[#134e4a] text-white text-xs font-bold rounded-xl hover:bg-[#0f3d38] cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
